import type { DynamicTableColumnConfig, DynamicTableConfig } from '../../config/types';

const hasValue = (value: string | undefined) => value !== undefined && value !== '';
const MANUAL_ROW_COUNT = 'manual';
const ROW_COUNT_AUTO_VALUE_PREFIX = '__rowCountAutoValue__';

const parseAnswer = (answer: unknown): Record<string, string> => {
  if (typeof answer === 'string') {
    try {
      const parsed = JSON.parse(answer);
      return parsed && typeof parsed === 'object' && !Array.isArray(parsed)
        ? Object.fromEntries(
            Object.entries(parsed).map(([key, value]) => [key, String(value ?? '')]),
          )
        : {};
    } catch (_) {
      return {};
    }
  }

  return answer && typeof answer === 'object' && !Array.isArray(answer)
    ? Object.fromEntries(Object.entries(answer).map(([key, value]) => [key, String(value ?? '')]))
    : {};
};

export const getDynamicTableCellFieldName = (rowKey: string, columnKey: string) =>
  `${rowKey}${columnKey}`;

export const getDynamicTableRowCountAutoValueFieldName = (
  rowKey: string,
  targetColumn: string,
) => `${ROW_COUNT_AUTO_VALUE_PREFIX}${getDynamicTableCellFieldName(rowKey, targetColumn)}`;

export const markDynamicTableRowCountAsManual = (
  config: DynamicTableConfig,
  rowKey: string,
  columnKey: string,
  values: Record<string, string>,
): Record<string, string> => {
  if (!config.rowCountsFrom || columnKey !== config.rowCountsFrom.targetColumn) return values;

  return {
    ...values,
    [getDynamicTableRowCountAutoValueFieldName(rowKey, columnKey)]: MANUAL_ROW_COUNT,
  };
};

export const getDynamicTableColumnStoredFieldNames = (
  rowKey: string,
  column: DynamicTableColumnConfig,
) => {
  const fieldNames = [getDynamicTableCellFieldName(rowKey, column.key)];
  if (column.inputType !== 'duration' || !column.duration) return fieldNames;

  return [
    ...fieldNames,
    getDynamicTableCellFieldName(rowKey, column.duration.unitKey),
    getDynamicTableCellFieldName(rowKey, column.duration.durationDaysKey),
  ];
};

export const getDynamicTableColumnEditableFieldNames = (
  rowKey: string,
  column: DynamicTableColumnConfig,
) => {
  const fieldNames = [getDynamicTableCellFieldName(rowKey, column.key)];
  if (column.inputType !== 'duration' || !column.duration) return fieldNames;

  return [...fieldNames, getDynamicTableCellFieldName(rowKey, column.duration.unitKey)];
};

export const formatDynamicTableCellValue = (
  rowKey: string,
  column: DynamicTableColumnConfig,
  values: Record<string, string>,
) => {
  const value = values[getDynamicTableCellFieldName(rowKey, column.key)];
  if (column.inputType !== 'duration' || !column.duration) return value;

  const unit = values[getDynamicTableCellFieldName(rowKey, column.duration.unitKey)];
  return [value, unit].filter(Boolean).join(' ');
};

export const applyDynamicTableDurationValues = (
  config: DynamicTableConfig,
  values: Record<string, string>,
): Record<string, string> => {
  const nextValues = { ...values };

  config.rows.forEach((row) => {
    config.columns.forEach((column) => {
      if (column.inputType !== 'duration' || !column.duration) return;

      const durationField = getDynamicTableCellFieldName(row.key, column.key);
      const unitField = getDynamicTableCellFieldName(row.key, column.duration.unitKey);
      const durationDaysField = getDynamicTableCellFieldName(
        row.key,
        column.duration.durationDaysKey,
      );
      const duration = nextValues[durationField];
      const unitValue = nextValues[unitField] || column.duration.defaultUnit;
      const unit = column.duration.units.find((option) => option.value === unitValue);

      if (!hasValue(duration) || !unit) {
        delete nextValues[durationDaysField];
        return;
      }

      const numericDuration = Number(duration);
      if (!Number.isFinite(numericDuration)) {
        delete nextValues[durationDaysField];
        return;
      }

      nextValues[unitField] = unit.value;
      nextValues[durationDaysField] = String(numericDuration * unit.days);
    });
  });

  return nextValues;
};

export const prepopulateDynamicTableRowCounts = (
  config: DynamicTableConfig,
  sourceAnswer: unknown,
  currentValues: Record<string, string>,
): Record<string, string> => {
  if (!config.rowCountsFrom) return currentValues;

  const sourceValues = parseAnswer(sourceAnswer);
  const counts = Object.entries(sourceValues)
    .filter(
      ([field, value]) => field.endsWith(config.rowCountsFrom?.sourceColumn ?? '') && value.trim(),
    )
    .reduce<Record<string, number>>((totals, [, value]) => {
      totals[value] = (totals[value] ?? 0) + 1;
      return totals;
    }, {});
  const nextValues = { ...currentValues };

  config.rows.forEach((row) => {
    const targetField = getDynamicTableCellFieldName(
      row.key,
      config.rowCountsFrom?.targetColumn ?? '',
    );
    const sourceValue = config.rowCountsFrom?.sourceValueByRow?.[row.key] ?? row.label;
    const autoValue = String(counts[sourceValue] ?? 0);
    const autoValueField = getDynamicTableRowCountAutoValueFieldName(
      row.key,
      config.rowCountsFrom?.targetColumn ?? '',
    );
    const currentValue = nextValues[targetField];
    const previousAutoValue = nextValues[autoValueField];

    if (!hasValue(currentValue)) {
      nextValues[targetField] = autoValue;
      nextValues[autoValueField] = autoValue;
      return;
    }

    if (previousAutoValue === MANUAL_ROW_COUNT) return;

    if (!hasValue(previousAutoValue)) {
      nextValues[autoValueField] = currentValue === autoValue ? autoValue : MANUAL_ROW_COUNT;
      return;
    }

    if (currentValue !== previousAutoValue) {
      nextValues[autoValueField] = MANUAL_ROW_COUNT;
      return;
    }

    nextValues[targetField] = autoValue;
    nextValues[autoValueField] = autoValue;
  });

  return nextValues;
};
